import {Injectable} from '@angular/core';
import {environment} from "../../../environments/environment";
import * as _ from "lodash";
import {Nestable} from "../model/summary";
import {HttpClient} from "@angular/common/http";
import {AppStateService} from "./app-state.service";
import {Router} from "@angular/router";
import {Datacenter} from "../model/datacenter";
import {Observable, of, tap} from "rxjs";
import {ColorUtils} from '../util/color-utils';
import {DatacenterV2} from "../model/datacenter-v2";
import mapboxgl from "mapbox-gl";

@Injectable({
  providedIn: 'root'
})
export class DataCentersService {

  constructor(private http: HttpClient, private appStateService: AppStateService) {

  }

  public getPaginatedDatacenters(mapId: string, pageSize: number, lastDatacenterId: string) {
    if (lastDatacenterId) {
      return this.http.get(`${environment.apiDatacentersUrl}/page/${mapId}?pageSize=${pageSize}&lastId=${lastDatacenterId}`, {responseType: 'json'}).pipe(

      );
    } else {
      return this.http.get(`${environment.apiDatacentersUrl}/page/${mapId}?pageSize=${pageSize}`, {responseType: 'json'}).pipe(

      );
    }

  }

  public getDatacentersWithErrors(mapId: string) {
    return this.http.get(`${environment.apiDatacentersUrl}/page/${mapId}?withErrors=true`, {responseType: 'json'}).pipe(

    );

  }

  public getDatacentersByProvider(mapId: string, provider: string) {
    return this.http.get(`${environment.apiDatacentersUrl}/page/${mapId}?provider=${provider}`, {responseType: 'json'});
  }


  public loadDataCenters(savedData: Datacenter[]): Datacenter[] {
    let dataCenters: Datacenter[] = [];
    for (let ob of savedData) {
      const data: any = {
        ...ob,
        entireData: [],
        auxId: 0,
        children: [],
        parent: null,
        expanded: false,
        isGlobal: false,
        label: ob.building,
        highligth: false
      };
      dataCenters.push(data);
    }
    return dataCenters;
  }

  public getDataCenters(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/data`, {responseType: 'json'}).pipe(
      tap(
        (input: any) => {
          let dataCenters: Datacenter[] = [];
          for (let ob of input.data) {
            const data: any = {
              ...ob,
              entireData: [],
              auxId: 0,
              children: [],
              parent: null,
              expanded: false,
              selected: false,
              isGlobal: false,
              color: ob.color || "",
              imageBase64: ob?.imageBase64,
              label: ob.building,
              highligth: false,
              totalMwPlan: 0,
              totalMwLive: 0,
              dataSource: ob.dataSource || [],
              siteType: ob.siteType || [],
              productType: ob.productType || [],
              buildingTenant: ob.buildingTenant || []
            };
            dataCenters.push(data);
          }
          return of(dataCenters);
        }
      ));
  }


  /**
   * Simple market area data without provider hierarchy - for market area visualization
   * Groups datacenters by 'area' field (Market Area) - both 'market' and 'area' are required
   */
  public getSimpleMarketsData(datacenters: Datacenter[]): Nestable[] {
    const marketAreaGroup: Nestable[] = [];

    // Filter datacenters that have both required fields: market AND area
    const validDatacenters = datacenters.filter(dc =>
      dc.market && dc.area &&
      dc.market.trim() !== '' && dc.area.trim() !== '' &&
      dc.market !== 'undefined' && dc.area !== 'undefined' &&
      dc.market !== 'null' && dc.area !== 'null'
    );

    // Group datacenters by 'area' (Market Area) - this is the key field for visualization
    const groupedByArea = _.groupBy(validDatacenters, 'area');

    // Create market area objects
    for (const areaName in groupedByArea) {
      const areaDatacenters = groupedByArea[areaName];

      const marketArea: Nestable = {
        children: [],
        parent: null,
        expanded: false,
        selected: false,
        label: areaName, // Display the area name as label
        isGlobal: false,
        color: "",
        imageBase64: null,
        totalMwPlan: areaDatacenters.reduce((sum, dc) => sum + (dc.mwPlanned || 0), 0),
        totalMwLive: areaDatacenters.reduce((sum, dc) => sum + (dc.mwLive || 0), 0),
        entireData: areaDatacenters // All datacenters in this market area
      };

      marketAreaGroup.push(marketArea);
    }

    // Sort market areas alphabetically
    return _.orderBy(marketAreaGroup, area => area.label.toLowerCase(), "asc");
  }

  public getGroupedBuildingsByMarket(datacenters: Datacenter[], keepColor: boolean = false) {
    const markets: Array<Nestable> = new Array<Nestable>();
    let x = 0;
    for (const selection of datacenters) {
      const existsMarket = !!markets.filter(tp => tp.label == selection.area && selection.market == tp.auxMarket).length;
      let market: Nestable;
      let provider: Nestable;
      if (existsMarket) {
        market = markets.filter(tp => tp.label == selection.area && selection.market == tp.auxMarket)[0];
        const existsProvider = !!market.children.filter(tp => tp.label == selection.provider).length;
        if (existsProvider) {
          provider = market.children.filter(tp => tp.label == selection.provider)[0];
        } else {
          provider = {
            children: [],
            expanded: false,
            isGlobal: false,
            label: selection.provider,
            parent: undefined,
            selected: false,
            color: selection.color,
            imageBase64: selection.imageBase64,
            totalMwPlan: 0,
            totalMwLive: 0,
            entireData: [],
          };
          market.children.push(provider);
        }
      } else {
        market = {
          children: [],
          expanded: false,
          isGlobal: false,
          label: selection.area,
          parent: undefined,
          selected: false,
          color: selection.color,
          imageBase64: selection.imageBase64,
          totalMwPlan: 0,
          totalMwLive: 0,
          entireData: datacenters.filter(d => d.area == selection.area && d.market == selection.market),
          auxMarket: selection.market
        };
        provider = {
          children: [],
          expanded: false,
          isGlobal: false,
          label: selection.provider,
          parent: undefined,
          selected: false,
          color: selection.color,
          imageBase64: selection.imageBase64,
          totalMwPlan: 0,
          totalMwLive: 0,
          entireData: [],
        };
        markets.push(market);
        market.children.push(provider);
      }
      selection.auxId = x++;
      provider.children.push(selection);
    }
    return markets;
  }

  public groupDatacentersByProvider(providers: Nestable[], selectedDatacenters: Datacenter[]) {
    const providersWithDatacenters: Nestable[] = [];

    providers.forEach(provider => {
      provider.color = provider.color || ColorUtils.generateRandomColor();
      provider.imageBase64 = provider?.imageBase64;

      if (!this.isProviderAlreadyGrouped(providersWithDatacenters, provider.label)) {
        provider.children = [];

        const providerDatacenters = selectedDatacenters
          .filter(datacenter => datacenter.provider === provider.label)
          .map(datacenter => this.assignProviderColorToDatacenter(datacenter, provider.color));

        provider.children.push(...providerDatacenters);

        providersWithDatacenters.push(provider);
      }
    });

    return providersWithDatacenters;
  }

  public getDatacenterFromFeature(feature: any): DatacenterV2 {
    return {
      ...feature.properties,
      buildingTenant: JSON.parse(feature.properties["buildingTenant"]),
      siteType: JSON.parse(feature.properties["siteType"]),
      productType: JSON.parse(feature.properties["productType"]),
      dataSource: JSON.parse(feature.properties["dataSource"]),
    };
  }

  public getFeatureCollectionFromSource(map: mapboxgl.Map, sourceId: string) {
    const source = map.getSource(sourceId) as any;
    const featureColletion = source._data;
    return [source, featureColletion];
  }

  private isProviderAlreadyGrouped(groupedProviders: Nestable[], providerLabel: string): boolean {
    return !!groupedProviders.find(groupedProvider => groupedProvider.label === providerLabel);
  }

  private assignProviderColorToDatacenter(datacenter: Datacenter, providerColor: string): Datacenter {
    datacenter.color = providerColor;
    return datacenter;
  }

}
